import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, Dropdown, Input, Checkbox, Typography, Tooltip } from '../../../../components';
import { useUserCustomization } from '../../../../hooks/useUserCustomization';
import { ReloadOutlined, BarsOutlined, FilterOutlined, CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { FILTER_BADGE_ICONS } from '../../Inventory/components/InventoryTopBar/constants';

import './BaseDiscoveryTopBar.scss';

const { Text } = Typography;
const { Search } = Input;

/**
 * BaseDiscoveryTopBar - A shared top bar component for Discovery pages
 * Used by both Inventory and MoveGroupsAndApps
 */
export const BaseDiscoveryTopBar = ({
    // Column selection
    selectedColumns,
    handleColumnSelection,
    selectableColumns,

    // Search
    setSearchValue,
    searchValue,
    searchPlaceholder = 'Search',
    searchTooltip,

    // Filters
    handleFilterData,
    resetFilters,
    selectedFilters,
    setSelectedFilters,
    filterOptions = [],

    // Data refresh
    refetchData,

    // Selected rows
    selectedRowKeys = [],

    // Custom actions - render props for flexibility
    renderImportSection,
    renderBulkActions,
    renderExtraActions,

    // Control visibility of search/filter controls
    showSearchAndFilters = true,

    className = '',
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

    // Filters that are currently being checked in the dropdown. The filtered request is not sent until Apply is clicked.
    const [pendingFilters, setPendingFilters] = useState(selectedFilters);
    const hasSelectedRows = selectedRowKeys.length > 0;

    const activeFilterButtonClass = classNames('rm-button-light', {
        'rm-button-active': selectedFilters.length > 0,
    });

    // When the selectedFilters prop changes, update the pendingFilters state to match, so that the dropdown reflects the current filters when opened.
    useEffect(() => {
        setPendingFilters(selectedFilters);
    }, [selectedFilters]);

    const filterBadges = useMemo(() => {
        return selectedFilters.map((filterValue) => ({
            badge: FILTER_BADGE_ICONS[filterValue],
            values: [filterValue],
        }));
    }, [selectedFilters]);

    const handleRemoveBadge = useCallback(
        (valuesToRemove) => {
            const newFilters = selectedFilters.filter((f) => !valuesToRemove.includes(f));
            setSelectedFilters(newFilters);
            handleFilterData(newFilters);
        },
        [selectedFilters, setSelectedFilters, handleFilterData],
    );

    const bulkActionContainerClasses = classNames('inventory-bulk-actions', {
        'inventory-bulk-actions--visible': hasSelectedRows,
    });

    const handleInputChange = useCallback(
        (e) => {
            const value = e.target.value;
            setSearchValue(value);
        },
        [setSearchValue],
    );

    useUserCustomization();

    const columnSelectionCheckboxes = selectableColumns.map((column) => ({
        key: column,
        label: (
            <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={selectedColumns.includes(column)}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleColumnSelection(column, e.target.checked);
                    }}
                >
                    {column}
                </Checkbox>
            </div>
        ),
    }));

    return (
        <div className={`base-discovery-top-bar ${className}`}>
            <div className="import-container">
                {/* Custom import section */}
                {renderImportSection && renderImportSection()}

                {/* Bulk actions section */}
                <div className={bulkActionContainerClasses}>
                    {renderBulkActions && renderBulkActions({ hasSelectedRows, selectedRowKeys })}
                </div>

                {/* Extra actions (ellipsis menu, etc.) */}
                {renderExtraActions && renderExtraActions({ hasSelectedRows })}
            </div>
            {showSearchAndFilters && (
                <div className="filters-and-controls">
                    {filterBadges.length > 0 && (
                        <div className="filter-badges-container">
                            <span className="filter-badges-spacer" />
                            {filterBadges.map(({ badge, values }) => (
                                <Button
                                    key={values.join(',')}
                                    className="rm-button-light filter-badge"
                                    shape="round"
                                    onClick={() => handleRemoveBadge(values)}
                                >
                                    <badge.Icon style={badge.style} /> {badge.label} <CloseOutlined />
                                </Button>
                            ))}
                        </div>
                    )}
                    {filterBadges.length > 0 && (
                        <Button className="rm-button-light" shape="round" onClick={() => resetFilters()}>
                            Clear All
                        </Button>
                    )}
                    <Button
                        className="rm-button-light"
                        shape="circle"
                        onClick={() => refetchData()}
                        icon={<ReloadOutlined />}
                    />
                    <Dropdown
                        classNames={{ root: 'inventory-selectable-columns-dropdown' }}
                        menu={{ items: columnSelectionCheckboxes }}
                        trigger={['click']}
                        placement="bottomRight"
                        open={dropdownOpen}
                        onOpenChange={(open) => setDropdownOpen(open)}
                    >
                        <Button shape="circle" icon={<BarsOutlined />} />
                    </Dropdown>
                    <Tooltip title={searchTooltip || searchPlaceholder} placement="bottom">
                        <Search
                            className="inventory-search-input"
                            allowClear
                            placeholder={searchPlaceholder}
                            value={searchValue}
                            onChange={handleInputChange}
                        />
                    </Tooltip>
                    {filterOptions.length > 0 && (
                        <Dropdown
                            classNames={{ root: 'inventory-filter-dropdown' }}
                            trigger={['click']}
                            placement="bottomLeft"
                            open={filterDropdownOpen}
                            onOpenChange={(open) => setFilterDropdownOpen(open)}
                            popupRender={() => (
                                <div className="inventory-filter-dropdown-content">
                                    <div className="filter-header">
                                        <Text>Filter by</Text>
                                    </div>
                                    <div className="filter-options">
                                        {filterOptions.map((filter) => (
                                            <Checkbox
                                                key={filter.value}
                                                checked={pendingFilters.includes(filter.value)}
                                                onChange={(e) => {
                                                    // When box is checked or unchecked, update the pending filters state so they can be applied when the user clicks "Apply"
                                                    const isChecked = e.target.checked;
                                                    setPendingFilters((prev) =>
                                                        isChecked
                                                            ? [...prev, filter.value]
                                                            : prev.filter((f) => f !== filter.value),
                                                    );
                                                }}
                                            >
                                                {filter.label}
                                            </Checkbox>
                                        ))}
                                    </div>
                                    <div className="filter-footer">
                                        <Button
                                            type="text"
                                            onClick={() => {
                                                setPendingFilters([]);
                                                resetFilters();
                                                setFilterDropdownOpen(false);
                                            }}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setSelectedFilters(pendingFilters);
                                                handleFilterData(pendingFilters);
                                                setFilterDropdownOpen(false);
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            )}
                        >
                            <Button className={activeFilterButtonClass} size="middle" icon={<FilterOutlined />} />
                        </Dropdown>
                    )}
                </div>
            )}
            {/* TODO: Implement search and filter controls for MoveGroupsAndApps */}
        </div>
    );
};
